import React, { useState, useEffect } from 'react';
import calibrationService, { CalibrationTable, CreateCalibrationTableRequest, UpdateCalibrationTableRequest, CalibrationPoint } from '../services/calibrationService';
import carService from '../services/carService';
import airportService from '../services/airportService';
import './CalibrationTableModal.css';

interface CalibrationTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  carId: number;
  table?: CalibrationTable | null;
  isEdit?: boolean;
}

const CalibrationTableModal: React.FC<CalibrationTableModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  carId,
  table,
  isEdit = false
}) => {
  const [formData, setFormData] = useState<{
    name: string;
    sensorNumber: number;
    sensorType: string;
    calibrationData: CalibrationPoint[];
  }>({
    name: '',
    sensorNumber: 1,
    sensorType: 'FLS485',
    calibrationData: [{ rawData: 0, liters: 0 }]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [cars, setCars] = useState<any[]>([]);
  const [selectedCarId, setSelectedCarId] = useState<number>(carId);
  const [loadingCars, setLoadingCars] = useState(false);
  const [airports, setAirports] = useState<any[]>([]);
  const [selectedAirportId, setSelectedAirportId] = useState<number | null>(null);
  const [loadingAirports, setLoadingAirports] = useState(false);

  // Убираем выбор типа датчика - используем только FLS485
  // const sensorTypes = calibrationService.getSensorTypes();

  useEffect(() => {
    if (table && isEdit) {
      setFormData({
        name: table.name,
        sensorNumber: table.sensorNumber,
        sensorType: table.sensorType,
        calibrationData: [...table.calibrationData]
      });
      setSelectedCarId(table.carId);
    } else {
      setFormData({
        name: '',
        sensorNumber: 1,
        sensorType: 'FLS485',
        calibrationData: [{ rawData: 0, liters: 0 }]
      });
      setSelectedCarId(carId);
    }
    setError(null);
    setValidationError(null);
    
    // Загружаем список машин
    const fetchCars = async () => {
      try {
        setLoadingCars(true);
        console.log('Загружаем машины...');
        const carsResponse = await carService.getCars(0, 1000);
        console.log('Получены машины:', carsResponse);
        setCars(carsResponse.content);
        console.log('Установлены машины в состояние:', carsResponse.content);
      } catch (err) {
        console.error('Error fetching cars:', err);
      } finally {
        setLoadingCars(false);
      }
    };
    fetchCars();

    // Загружаем список аэропортов
    const fetchAirports = async () => {
      try {
        setLoadingAirports(true);
        console.log('Загружаем аэропорты...');
        const airportsResponse = await airportService.getActiveAirports();
        console.log('Получены аэропорты:', airportsResponse);
        setAirports(airportsResponse);
        console.log('Установлены аэропорты в состояние:', airportsResponse);
      } catch (err) {
        console.error('Error fetching airports:', err);
      } finally {
        setLoadingAirports(false);
      }
    };
    fetchAirports();
  }, [table, isEdit, carId]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCalibrationDataChange = (index: number, field: keyof CalibrationPoint, value: number) => {
    setFormData(prev => ({
      ...prev,
      calibrationData: prev.calibrationData.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addCalibrationData = () => {
    setFormData(prev => ({
      ...prev,
      calibrationData: [...prev.calibrationData, { rawData: 0, liters: 0 }]
    }));
  };

  const removeCalibrationData = (index: number) => {
    if (formData.calibrationData.length > 1) {
      setFormData(prev => ({
        ...prev,
        calibrationData: prev.calibrationData.filter((_, i) => i !== index)
      }));
    }
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Название таблицы обязательно');
      return false;
    }

    if (formData.name.length < 2 || formData.name.length > 100) {
      setError('Название должно содержать от 2 до 100 символов');
      return false;
    }

    if (!selectedCarId) {
      setError('Необходимо выбрать машину');
      return false;
    }

    if (!selectedAirportId) {
      setError('Необходимо выбрать аэропорт');
      return false;
    }

    if (formData.sensorNumber < 1 || formData.sensorNumber > 10) {
      setError('Номер датчика должен быть от 1 до 10');
      return false;
    }

    if (formData.calibrationData.length < 2) {
      setError('Калибровочные данные должны содержать минимум 2 точки');
      return false;
    }

    // Валидация калибровочных данных
    const validation = calibrationService.validateCalibrationDataLocally(formData.calibrationData);
    if (!validation.isValid) {
      setValidationError(validation.message);
      return false;
    }

    setError(null);
    setValidationError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEdit && table) {
        const updateData: UpdateCalibrationTableRequest = {
          name: formData.name,
          sensorType: formData.sensorType,
          calibrationData: formData.calibrationData
        };
        await calibrationService.updateCalibrationTable(table.id, updateData);
      } else {
        const createData: CreateCalibrationTableRequest = {
          name: formData.name,
          sensorNumber: formData.sensorNumber,
          sensorType: formData.sensorType,
          carId: selectedCarId,
          calibrationData: formData.calibrationData
        };
        await calibrationService.createCalibrationTable(createData);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error saving calibration table:', err);
      setError(err.message || 'Ошибка при сохранении тарировочной таблицы');
    } finally {
      setLoading(false);
    }
  };

  const createDefaultTable = () => {
    const defaultTable = calibrationService.createDefaultCalibrationTable(selectedCarId, formData.sensorNumber);
    setFormData({
      name: defaultTable.name,
      sensorNumber: defaultTable.sensorNumber,
      sensorType: defaultTable.sensorType,
      calibrationData: defaultTable.calibrationData
    });
  };

  if (!isOpen) return null;

  console.log('Рендер модального окна. Машины:', cars);
  console.log('Выбранная машина:', selectedCarId);

  return (
    <div className="calibration-modal-overlay" onClick={onClose}>
      <div className="calibration-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="calibration-modal-header">
          <h2>{isEdit ? 'Редактировать тарировочную таблицу' : 'Создать тарировочную таблицу'}</h2>
          <button className="calibration-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="calibration-modal-form">
          <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Название таблицы *</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Тарировка датчика №1"
                required
                minLength={2}
                maxLength={100}
              />
            </div>
            <div className="form-group">
              <label htmlFor="carId">Машина *</label>
              <select
                id="carId"
                value={selectedCarId}
                onChange={(e) => setSelectedCarId(Number(e.target.value))}
                required
                disabled={isEdit || loadingCars}
              >
                <option value="">
                  {loadingCars ? 'Загрузка машин...' : 'Выберите машину'}
                </option>
                {cars.map(car => (
                  <option key={car.id} value={car.id}>
                    {car.name} {car.model} (№{car.number})
                  </option>
                ))}
              </select>
              {loadingCars && <small>Загружаем список машин...</small>}
            </div>
            <div className="form-group">
              <label htmlFor="airportId">Аэропорт *</label>
              <select
                id="airportId"
                value={selectedAirportId || ''}
                onChange={(e) => setSelectedAirportId(Number(e.target.value))}
                required
                disabled={isEdit || loadingAirports}
              >
                <option value="">
                  {loadingAirports ? 'Загрузка аэропортов...' : 'Выберите аэропорт'}
                </option>
                {airports.map(airport => (
                  <option key={airport.id} value={airport.id}>
                    {airport.displayName} ({airport.code})
                  </option>
                ))}
              </select>
              {loadingAirports && <small>Загружаем список аэропортов...</small>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sensorNumber">Номер датчика *</label>
              <input
                type="number"
                id="sensorNumber"
                value={formData.sensorNumber}
                onChange={(e) => handleInputChange('sensorNumber', Number(e.target.value))}
                min={1}
                max={10}
              required
                disabled={isEdit}
            />
            </div>
            <div className="form-group">
              <label htmlFor="sensorType">Тип датчика *</label>
              <div className="sensor-type-display">
                <span className="sensor-type-value">FLS485</span>
              </div>
            </div>
          </div>

          <div className="form-group">
            <div className="calibration-data-header">
              <label>Данные калибровки *</label>
              <div className="calibration-data-actions">
                <button
                  type="button"
                  onClick={createDefaultTable}
                  className="default-data-button"
                  disabled={isEdit}
                >
                  Стандартные данные
                </button>
              <button
                type="button"
                onClick={addCalibrationData}
                className="add-data-button"
              >
                  + Добавить точку
              </button>
              </div>
            </div>
            
            <div className="calibration-data-list">
              {formData.calibrationData.map((data, index) => (
                <div key={index} className="calibration-data-row">
                  <div className="data-input-group">
                    <input
                      type="number"
                      value={data.rawData}
                      onChange={(e) => handleCalibrationDataChange(index, 'rawData', Number(e.target.value))}
                      placeholder="Сырые данные"
                      min="0"
                      step="0.1"
                      required
                    />
                    <span className="data-separator">→</span>
                    <input
                      type="number"
                      value={data.liters}
                      onChange={(e) => handleCalibrationDataChange(index, 'liters', Number(e.target.value))}
                      placeholder="Литры"
                      min="0"
                      step="0.1"
                      required
                    />
                  </div>
                  {formData.calibrationData.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCalibrationData(index)}
                      className="remove-data-button"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            <small className="form-help">
              Первая точка должна иметь rawData = 0.0. Значения rawData должны быть монотонно возрастающими.
            </small>
          </div>

          {error && (
            <div className="calibration-modal-error">
              <p>{error}</p>
            </div>
          )}

          {validationError && (
            <div className="calibration-modal-error">
              <p>{validationError}</p>
            </div>
          )}

          <div className="calibration-modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="calibration-modal-button secondary"
            >
              Отмена
            </button>
            <button
              type="submit"
              className="calibration-modal-button primary"
              disabled={loading}
            >
              {loading ? 'Сохранение...' : (isEdit ? 'Обновить' : 'Создать')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CalibrationTableModal; 